/**
 * @fileoverview GameStateService - Lightweight state management with event emitter pattern
 * Provides centralized state with pub/sub for decoupled module communication
 */

/**
 * Game state interface
 * @typedef {Object} State
 * @property {number} completedCount - Number of successfully matched pairs
 * @property {number} totalPairs - Total number of pairs in current round
 * @property {boolean} isGameComplete - Whether all pairs are matched
 * @property {boolean} isMuted - Audio mute state
 * @property {boolean} isDragging - Whether user is currently dragging
 */
interface State {
    completedCount: number;
    totalPairs: number;
    isGameComplete: boolean;
    isMuted: boolean;
    isDragging: boolean;
}

/**
 * Event callback function type
 * @typedef {Function} EventCallback
 * @param {any} data - Event data payload
 */
type EventCallback = (data?: any) => void;

/**
 * GameStateService - Central state manager with event emission
 * 
 * Provides pub/sub pattern for loose coupling between modules.
 * Modules can listen to state changes without direct dependencies.
 * 
 * @example
 * const gameState = new GameStateService();
 * gameState.on('completed', (count) => console.log('Completed:', count));
 * gameState.update('completedCount', 3); // Emits 'change:completedCount' and 'change'
 * 
 * @class
 */
export class GameStateService {
    /**
     * Event listeners registry
     * @type {Object.<string, EventCallback[]>}
     */
    private listeners: { [key: string]: EventCallback[] } = {};
    
    /**
     * Current game state
     * @type {State}
     */
    private state: State;
    
    /**
     * Initialize game state with defaults
     */
    constructor() {
        this.state = {
            completedCount: 0,
            totalPairs: 0,
            isGameComplete: false,
            isMuted: false,
            isDragging: false
        };
    }
    
    /**
     * Subscribe to an event
     * 
     * @param {string} event - Event name (e.g., 'change', 'change:completedCount', 'complete')
     * @param {EventCallback} callback - Function to call when event is emitted
     * @returns {Function} Unsubscribe function
     * 
     * @example
     * const unsubscribe = gameState.on('change:completedCount', (count) => {
     *   console.log('Completed pairs:', count);
     * });
     * // Later: unsubscribe();
     */
    on(event: string, callback: EventCallback): () => void {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        this.listeners[event].push(callback);
        
        // Return unsubscribe function
        return () => this.off(event, callback);
    }
    
    /**
     * Unsubscribe from an event
     * 
     * @param {string} event - Event name
     * @param {EventCallback} callback - The callback to remove
     */
    off(event: string, callback: EventCallback): void {
        if (!this.listeners[event]) return;
        
        this.listeners[event] = this.listeners[event].filter(
            (cb: EventCallback) => cb !== callback
        );
    }
    
    /**
     * Emit an event to all subscribers
     * 
     * @param {string} event - Event name
     * @param {any} data - Optional data to pass to listeners
     * 
     * @example
     * gameState.emit('success', { target: 'DS1', score: 10 });
     */
    emit(event: string, data?: any): void {
        if (!this.listeners[event]) return;
        
        this.listeners[event].forEach((callback: EventCallback) => {
            try {
                callback(data);
            } catch (error) {
                console.error(`Error in event listener for '${event}':`, error);
            }
        });
    }
    
    /**
     * Update a state value and emit change events
     * 
     * Emits two events:
     * - 'change:key' with the new value
     * - 'change' with { key, value, prevValue }
     * 
     * @param {keyof State} key - State property to update
     * @param {any} value - New value
     * 
     * @example
     * gameState.update('completedCount', 3);
     * // Emits: 'change:completedCount' with value 3
     * // Emits: 'change' with { key: 'completedCount', value: 3, prevValue: 2 }
     */
    update(key: keyof State, value: any): void {
        const prevValue = this.state[key];
        
        // Only update and emit if value actually changed
        if (prevValue === value) return;
        
        (this.state as any)[key] = value;
        
        // Emit specific change event
        this.emit(`change:${key}`, value);
        
        // Emit general change event with details
        this.emit('change', { key, value, prevValue });
        
        // Check for game completion
        if (key === 'completedCount' && value === this.state.totalPairs && this.state.totalPairs > 0) {
            this.update('isGameComplete', true);
            this.emit('complete', { completedCount: value, totalPairs: this.state.totalPairs });
        }
    }
    
    /**
     * Get current state value
     * 
     * @param {keyof State} key - State property to get
     * @returns {any} Current value
     * 
     * @example
     * const count = gameState.get('completedCount');
     */
    get(key: keyof State): any {
        return this.state[key];
    }
    
    /**
     * Get entire state object (read-only copy)
     * 
     * @returns {State} Copy of current state
     */
    getState(): State {
        return { ...this.state };
    }
    
    /**
     * Reset state to initial values
     * 
     * @param {number} totalPairs - Number of pairs for new round
     * 
     * @example
     * gameState.reset(5); // Start new round with 5 pairs
     */
    reset(totalPairs: number): void {
        const prevState = { ...this.state };
        
        this.state = {
            completedCount: 0,
            totalPairs: totalPairs,
            isGameComplete: false,
            isMuted: prevState.isMuted, // Preserve mute state
            isDragging: false
        };
        
        this.emit('reset', { totalPairs });
    }
    
    /**
     * Increment completed count (convenience method)
     * 
     * @example
     * gameState.incrementCompleted(); // completedCount++
     */
    incrementCompleted(): void {
        this.update('completedCount', this.state.completedCount + 1);
    }
}
